import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { enqueueSnackbar } from 'actions';
import clsx from 'clsx';
import moment from 'moment';
import { Button, TextField, Card, Grid, CardHeader, CardContent, Tabs, Tab, Typography, Box, Divider } from '@mui/material';
import _ from 'underscore';
import { SelectField } from 'components';
import { MultiSelectField } from 'components/SelectField';
import DatePicker from 'components/DatePicker/DatePicker';

import { SetFormState, GenerateShutdownV2Report } from '../../actions/Shutdown3gActions';

const useStyles = makeStyles(theme => ({
	root: {},
}));

const OutliersDialogForm = (props) => {
	const { className, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const [availableDates, setAvailableDates] = useState([]);
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.shutdown3gReducer.formState);
	const userId = useSelector(state => state.session.user.id);
	const userRole = useSelector(state => state.session.user.role);
	const clustersNames = useSelector((state) => state.shutdown3gReducer.clustersNames);

	const _handleChange = event => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
                    event.target.type === 'checkbox'
                    	? event.target.checked
                    	: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	const _handleDateChange = (value, name, type = 'date') => {
		let formattedValue = null;

		if (value) {
			if (type === 'date') {
				formattedValue = value.startOf('day').format('YYYY-MM-DD');
			} else {
				formattedValue = value.format('YYYY-MM-DD HH:mm:ss');
			}
		}

		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: formattedValue
			},
			touched: {
				...formState.touched,
				[name]: true
			}
		});
	};

	const dateRangeError = useMemo(() => {
		const { shutdownDate, postDate } = formState.values;
		if (shutdownDate && postDate) {
			const diff = moment(postDate).diff(moment(shutdownDate), 'days');
			if (diff < 7) return 'The interval between Pre Date and Post Date must be at least 7 days.';
		}
		return null;
	}, [formState.values.shutdownDate, formState.values.postDate]);

	const _generateReport = () => {
		if (dateRangeError) {
			dispatch(enqueueSnackbar({ message: dateRangeError, options: { variant: 'error' } }));
			return;
		}
		dispatch(GenerateShutdownV2Report(userId, formState.values.clusterName, formState.values.shutdownDate, formState.values.postDate));
	};

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					<Card>
						<CardHeader title="Outliers V2" style={{ paddingBottom: 0 }} />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item spacing={2} md={2} xs={2}>
									<SelectField
										id="clusterName"
										name="clusterName"
										hintText="Cluster Name"
										defaultValue=""
										menuItems={(clustersNames) || []}
										value={formState.values.clusterName}
										onChange={_handleChange}
										sx={{
											'& .MuiInputBase-input': {
												backgroundColor: 'white',
												'&.Mui-disabled': {
													color: 'dimGray',
													WebkitTextFillColor: 'dimGray'
												}
											},
										}}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<DatePicker
										id="shutdownDate"
										name="shutdownDate"
										label="Pre Date"
										value={formState.values.shutdownDate}
										onChange={value => _handleDateChange(value, 'shutdownDate', 'date')}
										format="DD/MM/YYYY"
										disabled={formState.values.clusterName === undefined}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<DatePicker
										id="postDate"
										name="postDate"
										label="Post Date"
										value={formState.values.postDate}
										onChange={value => _handleDateChange(value, 'postDate', 'date')}
										format="DD/MM/YYYY"
										disabled={formState.values.clusterName === undefined}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<Button
										onClick={_generateReport}
										color="secondary"
										size="small"
										variant="contained"
										style={{ height: '100%' }}
										disabled={!(formState.values.postDate && formState.values.shutdownDate) || !!dateRangeError}
									>
										Generate Report
									</Button>
								</Grid>
							</Grid>
							<Box sx={{ px: 2, pb: 1 }}>
								<Typography variant="caption" color="text.secondary">
									7 days before Pre and Post Date
								</Typography>
								{dateRangeError && (
									<Typography variant="caption" color="error" sx={{ ml: 2 }}>
										{dateRangeError}
									</Typography>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div >
	);
};

OutliersDialogForm.propTypes = {
	className: PropTypes.string
};

export default OutliersDialogForm;
